import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import BlockUi from 'react-block-ui';
import MainLayout from './Main';
import AuthLayout from './Auth';
import BlankLayout from './Blank/index';
import { removeNotification } from '../components/Notification';
import SubscriptionWarningBanner from '../components/SubscriptionWarning';
import { appRoutes } from '@/routes';
import PendingInviteOrOnboardingRedirect from './PendingInviteOrOnboardingRedirect';
import PostAuthLandingRedirect from './PostAuthLandingRedirect';
import { SubscriptionWarningNonAdmin } from '@/components/SubscriptionWarning/SubscriptionWarningNonAdmin.component';
import { SubscriptionSeatsWarning } from '@/components/SubscriptionWarning/SubscriptionSeatsWarning.component';

const Layouts = {
  auth: AuthLayout,
  main: MainLayout,
  blank: BlankLayout,
};
export const nonActiveSubscriptionStates = [
  'cancelled',
  'expired',
  'suspended',
  'inactive',
  'halted',
];
const isDev = process.env.NODE_ENV === 'development';

/** @returns {'blank' | 'auth' | 'main'} */
function getLayoutKey(pathname) {
  const routes = appRoutes();
  const lower = pathname?.toLowerCase() ?? '';
  const blankPaths = [
    routes.addCompany,
    routes.selectCompany,
    routes.onboarding,
  ];
  if (blankPaths.includes(lower)) {
    return 'blank';
  }
  if (pathname === '/' || pathname === routes.invitation) {
    return 'auth';
  }
  if (/^\/auth(?=\/|$)/i.test(pathname)) {
    return 'auth';
  }
  return 'main';
}

function isBlankOnboardingCompanyPath(pathname, routes) {
  return (
    pathname === routes.addCompany ||
    pathname === routes.selectCompany ||
    pathname === routes.onboarding
  );
}

/** User may stay on these paths without subscription-suspended redirect */
function isSubscriptionBillingExemptPath(pathname, routes) {
  return (
    pathname === routes.subscriptionSuspended ||
    pathname === routes.subscription ||
    pathname === routes.payment
  );
}

function subscriptionSuspendedRedirectTo(activeCompany) {
  if (!activeCompany?.companyId) {
    return null;
  }
  return `/subscription-suspended?companyId=${activeCompany.companyId}`;
}

/**
 * @returns {string | null} redirect `to` value, or null if no redirect
 */
function getSubscriptionGateRedirect({
  pathname,
  activeCompany,
  subscription,
  routes,
  now = new Date(),
}) {
  if (!activeCompany?.companyId || !subscription) {
    return null;
  }

  if (
    nonActiveSubscriptionStates.includes(subscription.subscriptionStatus) &&
    subscription?.subscriptionEndDate?.toDate() < new Date() &&
    !isSubscriptionBillingExemptPath(pathname, routes)
  ) {
    return subscriptionSuspendedRedirectTo(activeCompany);
  }

  if (
    subscription.isTrialActive === true &&
    subscription.trialExpiryDate &&
    !isSubscriptionBillingExemptPath(pathname, routes)
  ) {
    const trialExpiry = new Date(subscription.trialExpiryDate);
    if (trialExpiry < now) {
      return subscriptionSuspendedRedirectTo(activeCompany);
    }
  }
  return null;
}

function logSubscriptionDev(message, payload) {
  if (!isDev) return;
  console.log(`[Layout] ${message}`, payload);
}

/**
 * Ordered guard chain for shell + auth + onboarding + subscription + post-login redirect.
 * First matching rule wins.
 */
function renderBootstrappedLayout({
  children,
  auth,
  pathname,
  routes,
  planFeatures,
}) {
  const layoutKey = getLayoutKey(pathname);
  const Container = Layouts[layoutKey];
  const isLoginLayout = layoutKey === 'auth';

  const isUserAuthorized = auth.authorized;
  const isUserLoading = auth.loading;
  const isPublicInvitationRoute = pathname === routes.invitation;
  const isNoCompanyAllowedPath =
    pathname === routes.invitationsHub ||
    pathname === routes.downloadDesktopApp ||
    pathname === routes.addCompany ||
    pathname === routes.selectCompany ||
    pathname === routes.onboarding;

  // Authorized users on blank flows (company / onboarding) — render without further gates
  if (isUserAuthorized && isBlankOnboardingCompanyPath(pathname, routes)) {
    return <Container>{children}</Container>;
  }

  // Main app: wait for account while unauthenticated
  if (isUserLoading && !isUserAuthorized && !isLoginLayout) {
    return (
      <div className="loader-container">
        <BlockUi tag="div" blocking message="Loading, please wait" />
      </div>
    );
  }

  if (!isLoginLayout && !isUserAuthorized) {
    return <Redirect to={routes.login} />;
  }

  // Multi-company (or any company) but no active company — pick workspace before onboarding / main app.
  if (isUserAuthorized && !isLoginLayout) {
    const companyList = Array.isArray(auth?.profile?.companyList)
      ? auth.profile.companyList
      : [];
    const activeCompanyId = auth?.profile?.activeCompany?.companyId;
    if (!activeCompanyId) {
      if (
        companyList.length > 0 &&
        pathname !== routes.selectCompany &&
        pathname !== routes.invitationsHub &&
        pathname !== routes.downloadDesktopApp &&
        pathname !== routes.invitation
      ) {
        return <Redirect to={routes.selectCompany} />;
      }
      if (companyList.length === 0 && !isNoCompanyAllowedPath) {
        return <Redirect to={routes.invitationsHub} />;
      }
    }
  }

  const onboardingDone =
    auth?.profile?.onboardingData?.onboardingCompleted !== false;
  if (
    isUserAuthorized &&
    !onboardingDone &&
    pathname !== routes.invitation &&
    pathname !== routes.invitationsHub &&
    pathname !== routes.downloadDesktopApp
  ) {
    return <PendingInviteOrOnboardingRedirect email={auth?.profile?.email} />;
  }

  const isMemberOnManageSettings =
    isUserAuthorized &&
    !isLoginLayout &&
    pathname === routes.manageSettings &&
    auth?.profile?.activeCompany?.role === 'Member';

  if (isMemberOnManageSettings) {
    return <Redirect to={routes.dashboardOverview} />;
  }

  if (isUserAuthorized && !isLoginLayout) {
    const activeCompany = auth?.profile?.activeCompany;
    const subscription = auth?.profile?.subscription;

    logSubscriptionDev('subscription gate inputs', {
      activeCompany,
      subscription,
      pathname,
    });

    const subscriptionRedirect = getSubscriptionGateRedirect({
      pathname,
      activeCompany,
      subscription,
      routes,
    });

    if (subscriptionRedirect) {
      logSubscriptionDev(
        'redirect → subscription-suspended',
        subscriptionRedirect
      );
      return <Redirect to={subscriptionRedirect} />;
    }
  }

  if (isLoginLayout && isUserAuthorized && !isPublicInvitationRoute) {
    removeNotification();
    return <PostAuthLandingRedirect profile={auth?.profile} />;
  }

  const isMemberOrManager = ['Member', 'Manager'].includes(
    auth?.profile?.activeCompany?.role
  );
  if (isMemberOrManager && pathname === appRoutes().subscription) {
    return <Redirect to={appRoutes().dashboardOverview} />;
  }
  if (isUserAuthorized && !isLoginLayout) {
    if (isMemberOrManager) {
      return (
        <Container>
          <SubscriptionWarningNonAdmin
            subscription={auth?.profile?.subscription}
          />
          {children}
        </Container>
      );
    }
    if (auth?.profile?.subscription?.isTrialActive) {
      return (
        <Container>
          <SubscriptionWarningBanner
            subscription={auth?.profile?.subscription}
          />
          {children}
        </Container>
      );
    }
    return (
      <Container>
        <SubscriptionSeatsWarning subscription={auth?.profile?.subscription} />
        {children}
      </Container>
    );
  }
  return <Container>{children}</Container>;
}

class IndexLayout extends Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  render() {
    const { children, auth, location, planFeatures } = this.props;
    const pathname = location?.pathname ?? '';
    const routes = appRoutes();

    return renderBootstrappedLayout({
      children,
      auth,
      pathname,
      routes,
      planFeatures,
    });
  }
}

IndexLayout.propTypes = {
  children: PropTypes.instanceOf(Object).isRequired,
  location: PropTypes.instanceOf(Object),
};

IndexLayout.defaultProps = {
  location: {},
};

export default connect(({ auth, planFeatures }) => ({ auth, planFeatures }))(
  withRouter(IndexLayout)
);
